import { useRef, useState } from "react";

import {
  faEdit,
  faFileLines,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { NavLink, useLocation } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import type { TemplateWithVersions } from "src/cases/queries/templates";
import { AddTemplateDropdown } from "src/cases/templates/AddTemplateDropdown";
import { getTemplateIcon } from "src/cases/templates/utils";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { Tooltip } from "src/design-system/Tooltip";
import { getUrlToCaseTemplateVersionsPage } from "src/router/urls";
import { useIsContentOverflows } from "src/utils/useIsContentOverflows";

const TemplateItem: React.FC<{
  template: TemplateWithVersions[number];
  orgId: string;
  wsId: string;
  selected: boolean;
  onEditRequest: (templateId: string) => void;
}> = ({ template, orgId, wsId, selected, onEditRequest }) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const nameRef = useRef<HTMLSpanElement>(null);
  const isOverflowing = useIsContentOverflows(nameRef);

  const templateName = template.attributes.template_name;

  let content = (
    <div
      className={twJoin(
        "group/template mr-2.5 flex h-8 min-w-0 items-center rounded-lg border border-transparent hover:bg-gray-50",
        selected && "bg-gray-100",
      )}
      data-loc={`case-template-${template.id}`}
    >
      <NavLink
        className="flex w-full min-w-0 items-center py-1.5 pl-2"
        to={getUrlToCaseTemplateVersionsPage(orgId, wsId, template.id)}
      >
        <div className="pr-1.5">
          <Icon
            icon={getTemplateIcon(template.attributes.icon) ?? faFileLines}
            size="xs"
          />
        </div>
        <span
          ref={nameRef}
          className="min-w-0 truncate text-left font-inter-medium-13px text-gray-800"
        >
          {templateName}
        </span>
      </NavLink>
      <EllipsisOptionsDropdown
        buttonClassName="text-gray-800 opacity-0 group-hover/template:opacity-100 data-[menu-open=true]:opacity-100 pr-2 py-1.5"
        dataLoc={`template-options-${template.id}`}
        elements={[
          {
            key: "Edit details",
            icon: faEdit,
            action: () => onEditRequest(template.id),
          },
        ]}
        iconSize="xs"
        placement="bottom start"
      />
    </div>
  );

  if (isOverflowing) {
    content = (
      <Tooltip
        open={tooltipOpen}
        onOpenChange={setTooltipOpen}
        placement="right"
        title={templateName}
        asChild
      >
        {content}
      </Tooltip>
    );
  }

  return <li className="w-full min-w-0">{content}</li>;
};

type TemplateNavListProps = {
  templates: TemplateWithVersions | undefined;
  isLoading: boolean;
  orgId: string;
  wsId: string;
  canCreate: boolean;
  displayAsNested?: boolean;
  onEditRequest: (templateId: string) => void;
};

export const TemplateNavList: React.FC<TemplateNavListProps> = ({
  templates,
  isLoading,
  orgId,
  wsId,
  canCreate,
  displayAsNested = false,
  onEditRequest,
}) => {
  const { pathname } = useLocation();
  const isTemplateActive = (templateId: string) =>
    pathname.includes(`/templates/${templateId}`);

  return (
    <div
      className={twJoin(
        "flex flex-col gap-y-2",
        displayAsNested && "ml-3 border-gray-200 border-l pl-1",
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {isLoading && (
        <div className="flex flex-col gap-y-2 px-2">
          <Skeleton height="h-6" width="w-full" />
          <Skeleton height="h-6" width="w-full" />
        </div>
      )}
      {!isLoading && (
        <ul className="decideScrollbarThin flex max-h-96 flex-col gap-y-0.5 overflow-y-auto overscroll-auto">
          {templates?.map((template) => (
            <TemplateItem
              key={template.id}
              onEditRequest={onEditRequest}
              orgId={orgId}
              selected={isTemplateActive(template.id)}
              template={template}
              wsId={wsId}
            />
          ))}
        </ul>
      )}
      {canCreate && (
        <AddTemplateDropdown buttonDataLoc="add-case-template">
          <button
            className="flex w-full items-center justify-start rounded-lg px-2 py-1.5 text-indigo-600 hover:bg-gray-50 focus-visible:outline-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pr-1.5">
              <Icon icon={faPlus} size="xs" />
            </div>
            <span className="grow truncate text-left font-inter-medium-13px">
              Add template
            </span>
          </button>
        </AddTemplateDropdown>
      )}
    </div>
  );
};
